import React, { useEffect, useState } from 'react';
import { friendlyFutureTime } from '../../lib/friendlyTime';

const API = process.env.REACT_APP_API_URL;

function frequencyLabel(f) {
  return { weekly: 'Weekly', biweekly: 'Every 2 weeks', monthly: 'Monthly', quarterly: 'Quarterly' }[f] || f;
}

function recipientLabel(s) {
  if (s.recipient_type === 'fdr') return s.fdr_name || 'FDR';
  return `${s.department_name || 'Department'} (internal)`;
}

function consumerLabel(c) {
  if (c === 'coa') return 'Results entry (COA Work Plan)';
  return c === 'scrubber' ? 'Audit (Scrubber)' : 'Reporting (Reporter)';
}

// Preserved for downstream consumers / future use — friendlyFutureTime now
// drives the visible "Next run" cell. Kept intentionally; do not remove.
// eslint-disable-next-line no-unused-vars
function fmtDateTime(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// eslint-disable-next-line no-unused-vars
function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export default function SchedulesList({ onNew }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [busy, setBusy] = useState(null);
  const [runMsg, setRunMsg] = useState('');

  // Fire every due cycle now instead of waiting for the hourly clock. Same
  // idempotent tick, so nothing double-sends.
  const runDue = async () => {
    setBusy('run-due'); setRunMsg('');
    try {
      const r = await fetch(`${API}/api/universe-requests/schedules/run-due`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Run failed');
      const failed = Array.isArray(d.failed) ? d.failed : [];
      setRunMsg(`${d.fired} cycle${d.fired === 1 ? '' : 's'} fired${failed.length ? `, ${failed.length} failed: ${failed.join('; ')}` : ''}${!d.fired && !failed.length ? ' (nothing was due)' : ''}`);
      load();
    } catch (err) { setRunMsg(err.message); } finally { setBusy(null); }
  };

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/universe-requests/schedules?include_disabled=${filter === 'all'}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setSchedules(d.schedules || []); setLoading(false); })
      .catch(() => { setSchedules([]); setLoading(false); });
  };
  useEffect(load, [filter]);

  const callAction = async (id, action, body = {}) => {
    setBusy(id);
    try {
      const method = action === 'delete' ? 'DELETE' : 'POST';
      const url = action === 'delete'
        ? `${API}/api/universe-requests/schedules/${id}`
        : `${API}/api/universe-requests/schedules/${id}/${action}`;
      const r = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: method === 'POST' ? JSON.stringify(body) : undefined,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Action failed');
      load();
    } catch (err) {
      alert(err.message);
    } finally { setBusy(null); }
  };

  const visible = filter === 'active'
    ? schedules.filter(s => s.enabled && !s.disabled_at)
    : filter === 'paused'
      ? schedules.filter(s => !s.enabled && !s.disabled_at)
      : schedules;

  return (
    <div>
      {/* Filter bar */}
      <div className="bg-white border border-slate-200 mb-4 flex items-center gap-1 px-2 py-2">
        {[
          { id: 'active', label: 'Active' },
          { id: 'paused', label: 'Paused' },
          { id: 'all',    label: 'All' },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1 text-xs font-mono uppercase tracking-wide transition-colors ${
              filter === f.id ? 'bg-red-700 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3">
          {runMsg && <span className="text-xs text-slate-600">{runMsg}</span>}
          <button onClick={runDue} disabled={busy === 'run-due'} title="Fire every cycle that is due right now instead of waiting for the hourly clock"
            className="px-3 py-1 text-xs font-semibold border border-slate-300 text-slate-700 hover:border-slate-500 disabled:opacity-50">
            {busy === 'run-due' ? 'Running…' : 'Run due cycles now'}
          </button>
          <div className="text-xs font-mono text-slate-400">
            {visible.length} {visible.length === 1 ? 'schedule' : 'schedules'}
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white border border-slate-200 p-12 text-center">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Loading…</div>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">No schedules yet</div>
          <p className="text-sm text-slate-600 mb-4">
            Schedules send a Universe Request to the same FDR or internal department on a repeating cadence —
            weekly, every two weeks, monthly, or quarterly.
          </p>
          <button
            onClick={onNew}
            className="bg-red-700 hover:bg-red-800 text-white px-4 py-1.5 text-sm font-semibold"
          >
            <span className="font-semibold">New Schedule</span>
          </button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-mono uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Recipient</th>
                <th className="text-left px-4 py-2">What</th>
                <th className="text-left px-4 py-2">Cadence</th>
                <th className="text-left px-4 py-2">Next run</th>
                <th className="text-left px-4 py-2">Cycles fired</th>
                <th className="text-left px-4 py-2">State</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(s => {
                const state = s.disabled_at
                  ? { label: 'Disabled', cls: 'bg-slate-100 text-slate-600' }
                  : s.enabled
                    ? { label: 'Active', cls: 'bg-emerald-100 text-emerald-800' }
                    : { label: 'Paused', cls: 'bg-amber-100 text-amber-800' };
                return (
                  <tr key={s.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-900">{s.name}</td>
                    <td className="px-4 py-3 text-slate-700">{recipientLabel(s)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <span className="text-xs font-mono text-slate-500">{consumerLabel(s.consumer_app)} · </span>
                      <span>{s.catalog_ids?.length || 0} items</span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{frequencyLabel(s.frequency)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <span title={s.next_run_at ? new Date(s.next_run_at).toLocaleString() : undefined}>
                        {friendlyFutureTime(s.next_run_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{s.run_count || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 text-xs font-mono uppercase tracking-wide ${state.cls}`}>{state.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!s.disabled_at && (
                        s.enabled ? (
                          <button
                            disabled={busy === s.id}
                            onClick={() => {
                              const reason = window.prompt('Reason for pausing (optional)') || null;
                              callAction(s.id, 'pause', { reason });
                            }}
                            className="text-xs text-slate-600 hover:text-slate-900 mr-3"
                          >Pause</button>
                        ) : (
                          <button
                            disabled={busy === s.id}
                            onClick={() => callAction(s.id, 'resume')}
                            className="text-xs text-emerald-700 hover:text-emerald-900 mr-3"
                          >Resume</button>
                        )
                      )}
                      {!s.disabled_at && (
                        <button
                          disabled={busy === s.id}
                          onClick={() => {
                            if (!window.confirm(`Disable schedule "${s.name}"? This cannot be undone. (Audit log preserved.)`)) return;
                            callAction(s.id, 'delete');
                          }}
                          className="text-xs text-red-700 hover:text-red-900"
                        >Disable</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
